import { Inject, Injectable } from '@angular/core';
import {
    AppNotificationService,
    AppNotificationType,
    ConfigConstants,
    ConfigInstanceToken,
    ConfigInterface,
    ENDPOINTS,
    isDefined,
    MaterialModel,
    SmartlockerDeliveryStatus,
    SmartlockerEndpoints,
} from '@evorait/pace-core';
import { catchError, forkJoin, map, Observable, of, Subject, take, throwError } from 'rxjs';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { HttpClient } from '@angular/common/http';
import { EnvironmentService } from '@evorait/pace-environment';
import moment from 'moment';
import { switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
    Delivery,
    SearchSmartlockerDeliveriesRequest,
    SearchSmartlockerDeliveriesResponse,
    SmartlockerMaterial,
    SmartLockerMaterialWithAccessCode,
} from '../interfaces/smartlocker-deliveries.interface';
import {
    GetSmartlockerDoorsRequest,
    GetSmartlockerDoorsResponse,
    GetSmartlockersResponse,
    SmartlockerData,
    SmartlockerDoorData,
} from '../interfaces/smartlocker-doors.interface';
import {
    SmartlockerAccessCodeRequest,
    SmartlockerAccessCodeResponse,
} from '../interfaces/smartlocker-access-code.interface';
import {
    SmartlockerMaterialSortingRequest,
    SmartlockerMaterialSortingResponse,
    SmartlockerSortingRequest,
    SmartlockerSortingResponse,
} from '../interfaces/smartlocker-sorting.interface';
import { SmartlockerStorageService } from './smartlocker-storage.service';
import {
    GetSmartlockerMaterialsRequest,
    GetSmartlockerMaterialsResponse,
    SetMaterialStatusRequest,
    SetMaterialStatusResponse,
} from '../interfaces/smartlocker-materials.interface';
import { BsModalService } from 'ngx-bootstrap/modal';

@Injectable({
    providedIn: 'root',
})
export class SmartlockerService {
    public refreshSmartlockerDeliveriesSubject = new Subject<void>();
    public smartlockerDeliveries$: Observable<Delivery[]>;
    private selectedDelivery: Delivery;
    private smartlockerDoorsData = new Map<number, SmartlockerDoorData[]>();
    private smartlockerAccessCodeData = new Map<number, SmartlockerAccessCodeResponse>();

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly httpClient: HttpClient,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        private readonly environmentService: EnvironmentService,
        private readonly smartlockerStorageService: SmartlockerStorageService,
        private readonly modalService: BsModalService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
    ) {
        this.smartlockerDeliveries$ = this.refreshSmartlockerDeliveriesSubject
            .asObservable()
            .pipe(switchMap(() => this.getSmartlockerDeliveries()));
    }

    /**
     * Triggers the prefetch process immediately after the initialization process is completed
     * if a preferred Smartlocker is present in the concurrency it reads it and kicks off the
     * background loading of the corresponding Smartlocker Doors and the Access codes
     *
     * @returns Observable undefined
     */
    public init(): Observable<void> {
        return this.getPreferredSmartlocker().pipe(
            switchMap((preferredSmartLocker) => {
                if (preferredSmartLocker.length === 0) {
                    return of(undefined);
                }
                return this.continuePrefetch(preferredSmartLocker);
            }),
        );
    }

    public setSelectedDelivery(delivery: Delivery): void {
        this.selectedDelivery = delivery;
    }

    public getSelectedDelivery(): Delivery {
        return this.selectedDelivery;
    }

    public getPreferredSmartlocker(): Observable<string> {
        return this.smartlockerStorageService.getConcurrentPreferredSmartlocker();
    }

    public savePreferredSmartlocker(smartlockerId: string): Observable<void> {
        this.smartlockerDoorsData.clear();
        this.smartlockerAccessCodeData.clear();
        return this.smartlockerStorageService.setConcurrentPreferredSmartlocker(smartlockerId);
    }

    public getSmartlockers(): Observable<SmartlockerData[]> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.SmartlockerBaseUrl}${SmartlockerEndpoints.GetSmartLockers}`;

        return this.httpClient.get<GetSmartlockersResponse>(url, {}).pipe(
            map((response) => response.Data),
            catchError((error) => {
                this.log.error('SmartlockerService: GetSmartlockers: Fetch smartlockers data API call failed: ', error);
                return throwError(() => error);
            }),
        );
    }

    /**
     * Retrieves an access code for a door, checking the local cache first.
     * This ensures that components display codes instantly if they were prefetched.
     * If not in cache, it triggers the generation API and caches the result.
     *
     * @param smartlockerId the smartlockerId
     * @param doorId the smartlockerDoorId
     * @returns SmartlockerAccessCodeResponse contains the barcode image and the access code
     */
    public getAccessCode(smartlockerId: number, doorId: number): Observable<SmartlockerAccessCodeResponse> {
        const cachedAccessCodes = this.smartlockerAccessCodeData.get(doorId);

        if (cachedAccessCodes) {
            this.log.debug(
                `SmartlockerService: getSmartlockerAccessCode: Found prefetched access code for door ${doorId}`,
            );
            return of(cachedAccessCodes);
        }

        return this.generateSmartlockerAccessCode(smartlockerId, doorId);
    }

    public generateSmartlockerAccessCode(
        smartlockerId: number,
        smartlockerDoorId: number,
    ): Observable<SmartlockerAccessCodeResponse> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.SmartlockerBaseUrl}${SmartlockerEndpoints.GenerateSmartLockerAccessCode}`;

        const request: SmartlockerAccessCodeRequest = {
            SmartlockerId: smartlockerId,
            SmartlockerDoorId: smartlockerDoorId,
            ActiveFrom: moment().format(ConfigConstants.formatPatternDateTimeSmartlocker),
            ActiveTo: moment()
                .add(ConfigConstants.defaultSmartlockerActiveToDays, 'days')
                .format(ConfigConstants.formatPatternDateTimeSmartlocker),
        };

        return this.httpClient.post<SmartlockerAccessCodeResponse>(url, request).pipe(
            tap((response) => this.smartlockerAccessCodeData.set(smartlockerDoorId, response)),
            catchError((error) => {
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('SMARTLOCKER_GET_ACCESS_CODE_ERROR_TITLE'),
                    `${<string>this.translate.instant('SMARTLOCKER_GET_ACCESS_CODE_ERROR_BODY')}`,
                );
                this.log.error(
                    'SmartlockerService: GenerateSmartlockerAccessCode: Fetch Access Code API call failed: ',
                    error,
                );
                return throwError(() => error);
            }),
        );
    }

    /**
     * Retrieves doors for a locker, checking the local cache first.
     * If the doors were prefetched or previously loaded, returns them immediately.
     * Otherwise, falls back to a network request.
     *
     * @param smartlockerId the smartlockerId
     * @returns the array of SmartlockerDoorData
     */
    public getDoorsForLocker(smartlockerId: number | undefined): Observable<SmartlockerDoorData[]> {
        if (!smartlockerId) {
            this.log.warn(
                'SmartlockerService: GetDoorsForLocker: getDoorsForLocker No ID provided, returning empty list.',
            );
            return of([]);
        }

        const cachedDoors = this.smartlockerDoorsData.get(smartlockerId);
        if (cachedDoors) {
            return of(cachedDoors);
        }

        return this.getSmartlockerDoors(smartlockerId);
    }

    public smartlockerSort(request: SmartlockerSortingRequest): Observable<SmartlockerSortingResponse> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.SmartlockerBaseUrl}${SmartlockerEndpoints.SmartLockerSorting}`;

        return this.httpClient.post<SmartlockerSortingResponse>(url, request).pipe(
            catchError((error) => {
                this.log.error('SmartlockerService: SmartlockerSort: Smartlocker sort API call failed', error);
                return throwError(() => error);
            }),
        );
    }

    public smartlockerMaterialSort(
        request: SmartlockerMaterialSortingRequest,
    ): Observable<SmartlockerMaterialSortingResponse> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.SmartlockerBaseUrl}${SmartlockerEndpoints.SmartLockerMaterialSorting}`;
        return this.httpClient.post<SmartlockerMaterialSortingResponse>(url, request).pipe(
            catchError((error) => {
                this.log.error(
                    'SmartlockerService: SmartlockerMaterialSort: Smartlocker Material sort API call failed',
                    error,
                );
                return throwError(() => error);
            }),
        );
    }

    public getMaterialsByMaterialNumberInSmartlocker(materialModel: MaterialModel): Observable<SmartlockerMaterial[]> {
        const smartlockerId = this.getSmartlockerIdFromStorageLocationDesc(materialModel.storageLocationDesc.value);
        const materialNumber = materialModel.materialIdentifier.materialNumber.value;
        const requestedQuantity = materialModel.quantity.value;

        if (!isDefined(requestedQuantity) || requestedQuantity <= 0) {
            const error = `SmartlockerService: getMaterialsByMaterialNumberInSmartlocker: missing or invalid requested quantity for material ${materialNumber}`;
            this.log.error(error);
            return throwError(() => error);
        }

        return this.getSmartlockerMaterials(smartlockerId).pipe(
            take(1),
            map((smartlockerMaterials) => smartlockerMaterials.filter((m) => m.MaterialNumber === materialNumber)),
            switchMap((matchingMaterials) => {
                if (matchingMaterials.length === 0 || matchingMaterials.length < requestedQuantity) {
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.INFO,
                        <string>this.translate.instant('SMARTLOCKER_PICKUP_CONFIRMATION_INSUFFICIENT_DIALOG_TITLE'),
                        <string>this.translate.instant(
                            'SMARTLOCKER_PICKUP_CONFIRMATION_DIALOG_INSUFFICIENT_NUMBER_OF_MATERIALS_BODY',
                            {
                                requestedQuantity: requestedQuantity,
                                foundMaterials: matchingMaterials?.length ?? 0,
                            },
                        ),
                    );
                    return of([]);
                }

                return of(matchingMaterials.slice(0, requestedQuantity));
            }),
            catchError((error) => {
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('SMARTLOCKER_GET_MATERIALS_ERROR_TITLE'),
                    `${<string>this.translate.instant('SMARTLOCKER_GET_MATERIALS_ERROR_BODY')}`,
                );
                this.log.error(
                    'SmartlockerService: GetSmartlockerMaterials: Fetch smartlocker Materials API call failed: ',
                    error,
                );
                return throwError(() => error);
            }),
        );
    }

    // status to 'Transferred'. One status entry is sent per requested unit. Access code is needed for the request
    public setMaterialsStatusToTransferred(materials: SmartLockerMaterialWithAccessCode[]): Observable<boolean> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.SmartlockerBaseUrl}${SmartlockerEndpoints.SetMaterialStatus}`;

        const request: SetMaterialStatusRequest = {
            MaterialStatusList: materials.map((material) => ({
                AccessCode: material.AccessCode,
                IlpnNo: material.ILPN,
                MaterialStatus: SmartlockerDeliveryStatus.Transferred,
            })),
        };

        return this.httpClient.post<SetMaterialStatusResponse>(url, request).pipe(
            map((setMaterialStatusResponse) => setMaterialStatusResponse.IsSuccess),
            tap((isSuccess) => {
                if (isSuccess) {
                    this.log.debug(
                        'SmartlockerService: setMaterialsStatusToTransferred: the material pickup process successfully completed',
                    );
                }
            }),
            catchError((error) => {
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('SMARTLOCKER_MATERIAL_PICKUP_TRANSFER_ERROR_TITLE'),
                    `${<string>this.translate.instant('SMARTLOCKER_MATERIAL_PICKUP_TRANSFER_ERROR_BODY')}`,
                );
                this.log.error(
                    'SmartlockerService: setMaterialsStatusToTransferred: the material pickup process failed to set the material status',
                    error,
                );
                return throwError(() => error);
            }),
        );
    }

    private continuePrefetch(id: string): Observable<void> {
        const smartlockerId = parseInt(id, 10);

        if (isNaN(smartlockerId)) {
            this.log.error('SmartlockerService: init: prefetch: Invalid smartlockerId format:', id);
            return of(undefined);
        }

        this.log.debug('SmartlockerService: init: Prefetching for ID:', smartlockerId);

        return this.fetchAndCacheDoorsForLocker(smartlockerId).pipe(
            catchError((error) => {
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('SMARTLOCKER_PREFETCH_ERROR_TITLE'),
                    <string>this.translate.instant('SMARTLOCKER_PREFETCH_ERROR_MESSAGE', {
                        errorMessage: error.message,
                    }),
                );
                return of(undefined);
            }),
        );
    }

    private getSmartlockerDeliveries(): Observable<Delivery[]> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.SmartlockerBaseUrl}${SmartlockerEndpoints.SearchSmartLockerDeliveries}`;

        const request: SearchSmartlockerDeliveriesRequest = {
            SearchString: '',
        };

        return this.httpClient.post<SearchSmartlockerDeliveriesResponse>(url, request).pipe(
            map((response) => {
                return response.Data;
            }),
            catchError((error) => {
                this.log.error(
                    'SmartlockerService: GetSmartlockerDeliveries: Search deliveries API call failed: ',
                    error,
                );
                return throwError(() => error);
            }),
        );
    }

    // Process the material that will be collected from the Smartlocker by setting its

    public getSmartlockerMaterials(smartlockerId: number): Observable<SmartlockerMaterial[]> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.SmartlockerBaseUrl}${SmartlockerEndpoints.GetSmartlockerMaterials}`;
        const request: GetSmartlockerMaterialsRequest = {
            SmartlockerId: smartlockerId,
        };
        return this.httpClient
            .post<GetSmartlockerMaterialsResponse>(url, request)
            .pipe(map((response) => response.Data));
    }

    private getSmartlockerDoors(smartlockerId: number): Observable<SmartlockerDoorData[]> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.SmartlockerBaseUrl}${SmartlockerEndpoints.GetSmartLockerDoors}`;

        const id = smartlockerId ?? this.selectedDelivery?.SmartlockerId;

        if (!id) {
            this.log.error('SmartlockerService: GetSmartlockerDoors: No SmartlockerId provided or found.');
            return throwError(() => new Error('SmartlockerService: GetSmartlockerDoors: No SmartlockerId provided'));
        }

        const request: GetSmartlockerDoorsRequest = {
            SmartlockerId: id,
        };

        return this.httpClient.post<GetSmartlockerDoorsResponse>(url, request).pipe(
            map((response) => response.Data),
            catchError((error) => {
                this.log.error(
                    'SmartlockerService: GetSmartlockerDoors: Getting Smartlocker doors API call failed',
                    error,
                );
                return throwError(() => error);
            }),
        );
    }

    /**
     * Executes a prefetch in the background of all doors for a given smartlocker
     * Chaining: Once doors are retrieved, it triggers parallel prefetch requests
     * for the access codes of every individual
     *
     * @param smartlockerId the smartlockerId to fetch its doors
     * @returns Observable<void>
     */
    private fetchAndCacheDoorsForLocker(smartlockerId: number): Observable<void> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.SmartlockerBaseUrl}${SmartlockerEndpoints.GetSmartLockerDoors}`;
        const request: GetSmartlockerDoorsRequest = { SmartlockerId: smartlockerId };

        return this.httpClient.post<GetSmartlockerDoorsResponse>(url, request).pipe(
            map((response) => response.Data),
            tap((doors) => {
                this.smartlockerDoorsData.set(smartlockerId, doors);
            }),
            switchMap((doors) => {
                if (!isDefined(doors) || doors.length === 0) {
                    return of(undefined);
                }
                const accessCodesRequests = doors.map((door) =>
                    this.fetchAndCacheAccessCode(smartlockerId, door.SmartlockerDoorId),
                );

                return forkJoin(accessCodesRequests).pipe(map(() => undefined));
            }),
            catchError((error) => {
                this.log.error(
                    `SmartlockerService: fetchDoorsForLocker: Failed to prefetch doors for preferred locker ${smartlockerId}`,
                    error,
                );
                return throwError(
                    () => new Error(`PACE could not fetch Smartlocker Doors for Smartlocker with id: ${smartlockerId}`),
                );
            }),
        );
    }

    /**
     * Fetches and caches an access code for a specific door.
     * Note: Uses the same date range logic as the public generation method
     * to ensure prefetched codes are valid.
     *
     * @param smartlockerId the smartlockerId
     * @param doorId the smartlockerDoorId
     * @returns Observable<void>
     */
    private fetchAndCacheAccessCode(smartlockerId: number, doorId: number): Observable<void> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.SmartlockerBaseUrl}${SmartlockerEndpoints.GenerateSmartLockerAccessCode}`;

        const request: SmartlockerAccessCodeRequest = {
            SmartlockerId: smartlockerId,
            SmartlockerDoorId: doorId,
            ActiveFrom: moment().format(ConfigConstants.formatPatternDateTimeSmartlocker),
            ActiveTo: moment()
                .add(ConfigConstants.defaultSmartlockerActiveToDays, 'days')
                .format(ConfigConstants.formatPatternDateTimeSmartlocker),
        };

        return this.httpClient.post<SmartlockerAccessCodeResponse>(url, request).pipe(
            tap((codeData) => {
                this.smartlockerAccessCodeData.set(doorId, codeData);
            }),
            map(() => undefined),
            catchError((error) => {
                this.log.error(
                    `SmartlockerService: fetchAndCacheAccessCode: Failed to prefetch access code for door ${doorId}`,
                    error,
                );
                return throwError(() => new Error(`PACE could not fetch Access Codes for Smartlocker doors`));
            }),
        );
    }

    public getSmartlockerIdFromStorageLocationDesc(value: string): number {
        const matchConfig = this.configService.getSmartlockerMaterialIdentifier();
        // const matchConfig = /SL(\d{5})/;
        const match = value.match(matchConfig);
        return match ? parseInt(match[1], 10) : 0;
    }

    public isSmartlockerMaterial(storageLocation: string): boolean {
        // const matchConfig = this.configService.getSmartlockerMaterialIdentifier();
        // const match = storageLocation.match(matchConfig);
        // return isDefined(match);
        return storageLocation.startsWith('SL');
    }
}
